import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Camera,
  Eye,
  EyeOff,
  MapPin,
  Plus,
  Save,
  Star,
} from "lucide-react";
import toast from "react-hot-toast";
import { Loading } from "notiflix";
import { ApiMediaURL } from "../../../setting/GlobleVariable";
import accountimage from "../../../assets/no_image_user.png";
import {
  TokenVerify,
  UpdateUserPassword,
  UpdateUserProfile,
  createAddressAction,
  listAddressAction,
  updateAddressAction,
} from "../../../redux/actions/User";
import CreateAddressModal from "../UserAddress/CreateAddressModal";
import {
  buildAddressPayload,
  mapAddressRecord,
} from "../../../utils/addressHelpers";

const INPUT_CLASS =
  "w-full px-4 py-3.5 rounded-xl bg-[#0a0e1a] border border-red-600/30 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:border-red-500/40 transition-all";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-white/50 uppercase tracking-wider mb-2 block font-black">
        {label}
      </label>
      {children}
    </div>
  );
}

function formatMemberSince(date) {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  } catch {
    return "—";
  }
}

function getInitials(firstName, lastName, email) {
  const f = (firstName || "").trim();
  const l = (lastName || "").trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f) return f.slice(0, 2).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return "IP";
}

export default function AccountDetails() {
  const user = useSelector((state) => state.user.user);
  const addresses = useSelector((state) => state.user.userAddress) || [];
  const isLoading = useSelector((state) => state.user.isLoading);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const [profileImage, setProfileImage] = useState(null);
  const [previewProfileImage, setPreviewProfileImage] = useState(null);
  const [profileSaved, setProfileSaved] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  const [showOldPass, setShowOldPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
  });

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    dispatch(listAddressAction());
  }, [dispatch]);

  useEffect(() => {
    if (!user) return;
    const primary = mapAddressRecord(addresses[0]);
    setForm((prev) => ({
      ...prev,
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      phone: user.phoneNumber || "",
      email: user.email || "",
      address: primary?.address || "",
      address2: primary?.address2 || "",
      city: primary?.city || "",
      state: primary?.state || "",
      zip: primary?.zip || "",
      country: primary?.country || "US",
    }));
  }, [user, addresses]);

  useEffect(() => {
    if (isLoading) {
      Loading.standard("Loading Please Wait", { svgColor: "#E52324" });
    } else {
      Loading.remove();
    }
  }, [isLoading]);

  const avatarSrc = previewProfileImage
    ? previewProfileImage
    : user?.Avatar
      ? `${ApiMediaURL}/${user.Avatar}`
      : accountimage;

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file.");
      return;
    }
    setProfileImage(file);
    setPreviewProfileImage(URL.createObjectURL(file));
  };

  const syncBillingAddress = async () => {
    const { address, city, state, zip } = form;
    if (!address?.trim() || !city?.trim() || !state?.trim() || !zip?.trim()) {
      return;
    }

    const payload = buildAddressPayload(form, user);
    const existingId = addresses[0]?._id;

    if (existingId) {
      await dispatch(updateAddressAction(existingId, payload));
    } else {
      await dispatch(createAddressAction(payload, () => {}));
      await dispatch(listAddressAction());
    }
  };

  const handleUpdateProfile = async () => {
    if (!form.firstName?.trim() || !form.lastName?.trim()) {
      toast.error("First and last name are required.");
      return;
    }

    const formdata = new FormData();
    formdata.append("firstName", form.firstName.trim());
    formdata.append("lastName", form.lastName.trim());
    formdata.append("phoneNumber", form.phone.trim());
    if (profileImage) formdata.append("file", profileImage);

    await dispatch(UpdateUserProfile(formdata));
    await syncBillingAddress();
    await dispatch(TokenVerify());
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 2000);
  };

  const updatePassword = () => {
    if (newPass !== confirmPass) {
      return toast.error("Confirm password does not match.");
    }
    if (!newPass || !currentPass || !confirmPass) {
      return toast.error("Please enter all password fields.");
    }
    dispatch(UpdateUserPassword(currentPass, newPass));
    setCurrentPass("");
    setNewPass("");
    setConfirmPass("");
  };

  const displayName = `${form.firstName} ${form.lastName}`.trim() || user?.email || "Member";

  return (
    <div className="max-w-3xl">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl font-black uppercase tracking-tight text-white mb-2">
          Account Details.
        </h1>
        <p className="text-white/40 text-sm mb-10">
          Manage your profile, password, and preferences
        </p>

        {/* Avatar */}
        <div className="flex flex-col items-center mb-10">
        <div className="relative">
            {user?.Avatar || previewProfileImage ? (
              <img
                src={avatarSrc}
            alt=""
                className="w-28 h-28 rounded-full object-cover border-[3px] border-red-600/50 shadow-xl shadow-red-600/20"
              />
            ) : (
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-red-600 to-red-800 border-[3px] border-red-600/50 flex items-center justify-center shadow-xl shadow-red-600/20">
                <span className="text-3xl font-black text-white">
                  {getInitials(form.firstName, form.lastName, form.email)}
                </span>
        </div>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-red-600 flex items-center justify-center shadow-lg shadow-red-600/30 hover:bg-red-700 transition-colors"
            >
              <Camera className="w-4 h-4 text-white" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="sr-only"
              onChange={handleImageChange}
            />
          </div>
          <div className="mt-4 text-center">
            <div className="text-white font-black">{displayName}</div>
            <div className="text-white/40 text-xs mt-1">
              Member since {formatMemberSince(user?.createdAt)}
            </div>
            <div className="flex items-center gap-1.5 mt-2 justify-center">
              <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
              <span className="text-xs font-black text-yellow-500">InstaPass Member</span>
            </div>
          </div>
        </div>

        <div className="h-px bg-red-600/20 mb-10" />

        {/* General Information */}
        <section className="mb-10">
          <h2 className="text-xl font-black uppercase tracking-tight text-white mb-2">
            General Information
          </h2>
          <p className="text-white/40 text-xs mb-6 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-red-500 shrink-0" />
            A valid billing address is required to receive tickets and process orders.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="First Name">
              <input
                value={form.firstName}
                onChange={(e) => update("firstName", e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
            <Field label="Last Name">
              <input
                value={form.lastName}
                onChange={(e) => update("lastName", e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
            <Field label="Mobile Number">
            <input
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
            <Field label="Email Address">
            <input
                type="email"
              readOnly
                value={form.email}
                className={`${INPUT_CLASS} opacity-70 cursor-not-allowed`}
              />
            </Field>
            <Field label="Address">
              <input
                value={form.address}
                onChange={(e) => update("address", e.target.value)}
                className={INPUT_CLASS}
                placeholder="Street address"
              />
            </Field>
            <Field label="Address 2">
              <input
                value={form.address2}
                onChange={(e) => update("address2", e.target.value)}
                className={INPUT_CLASS}
                placeholder="Apt, suite, etc."
              />
            </Field>
            <Field label="City">
              <input
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
            <Field label="State">
              <input
                value={form.state}
                onChange={(e) => update("state", e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
            <Field label="Zip Code">
              <input
                value={form.zip}
                onChange={(e) => update("zip", e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
            <Field label="Country">
              <input
                value={form.country}
                onChange={(e) => update("country", e.target.value)}
                className={INPUT_CLASS}
              />
            </Field>
          </div>

          <button
            type="button"
            onClick={handleUpdateProfile}
            disabled={isLoading}
            className="mt-6 inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-red-600 text-white text-sm font-black uppercase tracking-wider hover:bg-red-700 transition-colors shadow-lg shadow-red-600/20 disabled:opacity-50"
          >
            {profileSaved ? (
              <>
                <Save className="w-4 h-4" /> Saved!
              </>
            ) : (
              "Update"
            )}
          </button>
        </section>

        {/* Saved addresses from API */}
        <section className="mb-10">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-lg font-black uppercase tracking-tight text-white">
              Saved Addresses
            </h2>
        <button
              type="button"
              onClick={() => setShowAddressModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-red-600/30 text-red-500 text-xs font-bold uppercase tracking-wider hover:bg-red-600/10 transition-colors"
        >
              <Plus className="w-3.5 h-3.5" /> Add New
        </button>
      </div>

          {addresses.length === 0 ? (
            <p className="text-white/30 text-sm py-4 rounded-xl border border-dashed border-white/10 text-center">
              No saved addresses yet. Add one for faster checkout.
            </p>
          ) : (
            <div className="space-y-3">
              {addresses.map((item) => {
                const mapped = mapAddressRecord(item);
                return (
                  <button
                    key={item._id}
                    type="button"
                    onClick={() => {
                      if (!mapped) return;
                      setForm((prev) => ({
                        ...prev,
                        address: mapped.address,
                        address2: mapped.address2,
                        city: mapped.city,
                        state: mapped.state,
                        zip: mapped.zip,
                        country: mapped.country,
                      }));
                    }}
                    className="w-full text-left p-4 rounded-xl bg-[#0d1117] border border-red-600/15 hover:border-red-600/30 transition-colors"
                  >
                    <p className="text-white text-sm font-semibold mb-1">
                      {item.FullName || mapped?.address || "Address"}
                    </p>
                    <p className="text-white/40 text-xs leading-relaxed">
                      {[mapped?.address, mapped?.address2, mapped?.city, mapped?.state, mapped?.zip]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <div className="h-px bg-red-600/20 mb-10" />

        {/* Password */}
        <section className="mb-10">
          <h2 className="text-xl font-black uppercase tracking-tight text-white mb-6">
            Password
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="relative">
              <label className="text-xs text-white/50 uppercase tracking-wider mb-2 block font-black">
                Old Password
            </label>
            <input
                type={showOldPass ? "text" : "password"}
              value={currentPass}
              onChange={(e) => setCurrentPass(e.target.value)}
                placeholder="••••••••••"
                className={INPUT_CLASS}
              />
              <button
                type="button"
                onClick={() => setShowOldPass(!showOldPass)}
                className="absolute right-3 bottom-3.5 text-white/30 hover:text-white"
              >
                {showOldPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
          </div>
            <div className="relative">
              <label className="text-xs text-white/50 uppercase tracking-wider mb-2 block font-black">
                New Password
            </label>
            <input
                type={showNewPass ? "text" : "password"}
              value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
                placeholder="••••••••••"
                className={INPUT_CLASS}
              />
              <button
                type="button"
                onClick={() => setShowNewPass(!showNewPass)}
                className="absolute right-3 bottom-3.5 text-white/30 hover:text-white"
              >
                {showNewPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
          </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-white/50 uppercase tracking-wider mb-2 block font-black">
                Confirm Password
          </label>
          <input
            type="password"
            value={confirmPass}
                onChange={(e) => setConfirmPass(e.target.value)}
                placeholder="••••••••••"
                className={INPUT_CLASS}
          />
        </div>
          </div>

        <button
            type="button"
            onClick={updatePassword}
            className="mt-6 inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-red-600 text-white text-sm font-black uppercase tracking-wider hover:bg-red-700 transition-colors shadow-lg shadow-red-600/20"
        >
          Update
        </button>
        </section>
      </motion.div>

      {showAddressModal && <CreateAddressModal setHideModal={setShowAddressModal} />}
    </div>
  );
}
